import React from 'react';
import { Bell, Megaphone } from 'lucide-react';

type BrandRegionVisibility = Partial<Record<'zeelool' | 'vooglam', string[]>>;

export type NavChildItem = {
  path: string;
  label: string;
  permCode?: string;
  children?: NavChildItem[];
  hidden?: boolean;
  hiddenInClassic?: boolean;
  hiddenForBrands?: Array<'zeelool' | 'vooglam'>;
  hiddenForBrandRegions?: BrandRegionVisibility;
};

export type NavItem = {
  path: string;
  label: string;
  icon: React.FC;
  permCode: string;
  children?: NavChildItem[];
  hidden?: boolean;
  hiddenInClassic?: boolean;
  hiddenForBrands?: Array<'zeelool' | 'vooglam'>;
  hiddenForBrandRegions?: BrandRegionVisibility;
};

export type NavSection = {
  title: string;
  permCode: string;
  items: NavItem[];
};

function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function LocalAgentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8" />
      <path d="M12 16v4" />
      <path d="M9 10h6" />
      <path d="M12 7v6" />
    </svg>
  );
}

function CampaignIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M12 20V10" />
      <path d="M18 20V4" />
      <path d="M6 20v-4" />
    </svg>
  );
}

function AutopilotIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function BudgetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M12 1v22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M21 12a9 9 0 1 1-6.22-8.56" />
      <path d="M21 3v9h-9" />
    </svg>
  );
}

function InsightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function KnowledgeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function AdAnalysisIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="M7 10l3-3 3 2 4-4" />
    </svg>
  );
}

function AuthIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <circle cx="7.5" cy="15.5" r="3.5" />
      <path d="M11 12l8.5-8.5" />
      <path d="M17 6l2 2" />
      <path d="M15 8l2 2" />
    </svg>
  );
}

function PlatformIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

export const navSections: NavSection[] = [
  {
    title: 'Overview',
    permCode: 'overview',
    items: [
      {
        path: '/local-agent',
        label: '本地 Agent',
        icon: LocalAgentIcon,
        permCode: '',
        // 生产首发阶段暂不展示入口；页面、路由和接口继续保留，后续可直接恢复。
        hidden: true,
        children: [
          { path: '/local-agent/downloads', label: '安装包下载', permCode: '', hidden: true },
          { path: '/local-agent/releases', label: '安装包上传', permCode: 'ad:role:admin' },
        ],
      },
      { path: '/dashboard', label: '全局看板', icon: DashboardIcon, permCode: 'overview:dashboard' },
    ],
  },
  {
    title: '广告管理',
    permCode: 'ad-mgmt',
    items: [
      {
        path: '/campaigns',
        label: '广告计划',
        icon: CampaignIcon,
        permCode: 'ad-mgmt:campaigns',
        children: [
          { path: '/campaigns/facebook', label: 'Facebook 广告' },
          { path: '/campaigns/tiktok', label: 'TikTok 广告' },
          { path: '/campaigns/google', label: 'Google 广告' },
        ],
      },
      {
        path: '/ad-analysis',
        label: '营销分析',
        icon: AdAnalysisIcon,
        permCode: 'ad-mgmt:ad-analysis',
        // 已由数据统计下的营销分析入口替代，保留路由配置但不在菜单中展示。
        hidden: true,
        children: [
          { path: '/ad-analysis/tt-insights', label: 'TikTok数据', permCode: 'ad-mgmt:ad-analysis:tt', hidden: true },
          { path: '/ad-analysis/fb-insights', label: 'Facebook数据', permCode: 'ad-mgmt:ad-analysis:fb', hidden: true },
          { path: '/ad-analysis/gg-insights', label: 'Google数据', permCode: 'ad-mgmt:ad-analysis:gg', hidden: true },
        ],
      },
      {
        path: '/ad-analysis/ad-dashboard',
        label: '营销分析',
        icon: AnalyticsIcon,
        permCode: 'ad-mgmt:ad-analysis',
        children: [
          {
            path: '/ad-analysis/ad-dashboard/overview',
            label: '概览',
            children: [
                  { path: '/site-data/channel-portal', label: '渠道门户', permCode: 'stats:portal' },
                  { path: '/site-data/cost-type-monitoring', label: '花费类型', permCode: 'stats:cost-type', hiddenForBrandRegions: { zeelool: ['ca', 'uk', 'de', 'fr'] } },
                  { path: '/site-data/target-type-layered', label: '花费目标分层', permCode: 'stats:target-type', hiddenForBrands: ['vooglam'], hiddenForBrandRegions: { zeelool: ['ca', 'uk', 'de', 'fr'] } },
                  { path: '/site-data/delivery-target', label: '投放目标', permCode: 'stats:delivery-target', hiddenForBrands: ['vooglam'], hiddenForBrandRegions: { zeelool: ['ca', 'uk', 'de', 'fr'] } },
                  { path: '/site-data/channel-acquisition', label: '渠道拉新效果', permCode: 'stats:acquisition', hiddenForBrands: ['vooglam'] },
                  { path: '/ad-analysis/sku-roi', label: '商品ROI监控', permCode: 'stats:sku-roi' },
                  { path: '/ad-analysis/channel-wide-table', label: '渠道宽表', permCode: 'stats:channel-wide-table', hiddenForBrands: ['vooglam'] },
                  { path: '/ad-analysis/acquisition-cost', label: '获客成本', permCode: 'stats:acquisition-cost', hiddenForBrands: ['zeelool'] },
                  { path: '/site-data/traffic-monitoring', label: '流量监控', permCode: 'stats:traffic', hidden: true },
                  { path: '/site-data/bounce-rate', label: '网站跳出率', permCode: 'stats:bounce', hidden: true },
                  { path: '/site-data/user-portal', label: '用户门户', permCode: 'stats:user-portal', hidden: true },
            ],
          },
          {
            path: '/ad-analysis/ad-dashboard/delivery-details',
            label: '投放明细',
            children: [
                  { path: '/ad-analysis/ad-dashboard/google', label: 'Google投放明细', permCode: 'stats:delivery:google' },
                  { path: '/ad-analysis/ad-dashboard/meta', label: 'Meta投放明细', permCode: 'stats:delivery:meta' },
                  { path: '/ad-analysis/ad-dashboard/tiktok', label: 'TikTok投放明细', permCode: 'stats:delivery:tiktok' },
                  { path: '/ad-analysis/ad-dashboard/snapchat', label: 'Snapchat投放明细', permCode: 'stats:delivery:snapchat' },
                  { path: '/ad-analysis/ad-dashboard/bing', label: 'Bing投放明细', permCode: 'stats:delivery:bing' },
                  { path: '/ad-analysis/ad-dashboard/criteo', label: 'Criteo投放明细', permCode: 'stats:delivery:criteo' },
                  { path: '/ad-analysis/ad-dashboard/applovin', label: 'Applovin投放明细', permCode: 'stats:delivery:applovin' },
                  { path: '/ad-analysis/ad-dashboard/rtb', label: 'RTB投放明细', permCode: 'stats:delivery:rtb' },
            ],
          },
        ],
      },
      { path: '/autopilot', label: 'AI 智能投手', icon: AutopilotIcon, permCode: 'ad-mgmt:autopilot' },
      { path: '/budgets', label: '预算管理', icon: BudgetIcon, permCode: 'ad-mgmt:budgets' },
    ],
  },
  {
    title: '数据 & 知识',
    permCode: 'data',
    items: [
      { path: '/insights', label: '市场洞察', icon: InsightIcon, permCode: 'data:insights', hiddenInClassic: true },
      { path: '/analytics', label: '数据归因', icon: AnalyticsIcon, permCode: 'data:analytics', hiddenInClassic: true },
      { path: '/knowledge', label: '经验银行', icon: KnowledgeIcon, permCode: 'data:knowledge' },
    ],
  },
  {
    title: '系统管理',
    permCode: 'system',
    items: [
      { path: '/auth-accounts', label: '授权管理', icon: AuthIcon, permCode: 'system:auth-accounts' },
      { path: '/platform-accounts', label: '平台账号', icon: PlatformIcon, permCode: 'system:platform-accounts' },
      { path: '/notifications', label: '通知', icon: () => <Bell size={18} />, permCode: '' },
      { path: '/system/notifications', label: '通知管理', icon: () => <Megaphone size={18} />, permCode: 'ad:role:admin' },
      { path: '/usage-analytics', label: '使用日志', icon: AnalyticsIcon, permCode: 'ad:role:admin' },
    ],
  },
];

export function findNavLabelByPath(pathname: string): string | null {
  let matched: { path: string; label: string } | null = null;

  const visitChildren = (children: NavChildItem[]) => {
    for (const child of children) {
      if (pathname === child.path || pathname.startsWith(child.path + '/')) {
        if (!matched || child.path.length > matched.path.length) {
          matched = { path: child.path, label: child.label };
        }
      }
      if (child.children?.length) {
        visitChildren(child.children);
      }
    }
  };

  for (const section of navSections) {
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        if (!matched || item.path.length > matched.path.length) {
          matched = { path: item.path, label: item.label };
        }
      }
      visitChildren(item.children ?? []);
    }
  }

  return matched?.label ?? null;
}
